import React, { forwardRef } from 'react';
import { Button as BootstrapButton } from 'react-bootstrap';
import { kebabCase } from 'lodash';
import { AccessControlWrapper } from '../AccessControlWrapper';
import { FAIcon } from '../fa-icon';
import { InfoTooltip } from '../InfoTooltip';
import { formatClassName, pickDataAttributes } from '../../utils';
import { DataAttributes } from '../../utils/pickDataAttributes';
import { Props } from './types';

/** renders a button */
export const Button = forwardRef<HTMLButtonElement, Props>((props, forwardedRef) => {
  const {
    block,
    branded,
    children,
    className,
    cssStyle,
    data = {},
    disabled,
    download,
    fontTheme,
    href,
    icon,
    iconFamily,
    iconProps,
    label,
    onClick,
    permissions,
    requiredPermission,
    rightIcon,
    rightIconProps,
    style = 'primary',
    tooltipAttributes,
    variant,
    ...additionalProps
  } = props;

  const buttonClassName: string =
    formatClassName([
      fontTheme || 'ui-default',
      className,
      {
        [`btn-${style}-branded`]: branded,
        'btn-block': block,
        'no-label': !label,
      },
    ]) || '';

  const dataProps: { [prop: string]: string | number } = {};
  if (data.action) {
    dataProps['data-action'] = data.action;
  }
  if (data.objectId) {
    dataProps['data-object-id'] = data.objectId;
  }
  if (data.objectType) {
    dataProps['data-object-type'] = data.objectType;
  }
  if (data.componentType) {
    dataProps['data-component-type'] = data.componentType;
  }
  if (href) {
    dataProps['data-href'] = href;
  } else {
    dataProps['data-component-type'] = label ? `${kebabCase(label)}-button` : 'button';
  }
  const spin: boolean = !!icon && icon === 'spinner';

  const dataAttributes: DataAttributes = pickDataAttributes(props);

  const buttonVariant = style || variant;

  const onInternalClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (additionalProps?.target === '_blank') {
      window.open(href, additionalProps?.target, 'noopener,noreferrer');
    } else if (href) {
      window.location.assign(href);
    }
    if (onClick) {
      onClick(event);
    }
  };

  const accessControlProps: any = {
    permissions,
    requiredPermission,
    ...additionalProps,
  };

  const downloadProps: any = {};

  if (download) {
    downloadProps.download = download;
    downloadProps.href = href;

    if (!href) {
      console.error(
        "'href' is required when using 'download', considering adding it or removing 'download' if you use onClick to perform the download process."
      );
    }
  }

  const foundryButtonProps: any = {
    ...additionalProps,
    ...dataProps,
    ...dataAttributes,
    ...downloadProps,
    className: buttonClassName,
    isDisabled: disabled,
    variant: buttonVariant,
    style: cssStyle,
    ref: forwardedRef,
  };

  return (
    <AccessControlWrapper {...accessControlProps}>
      <BootstrapButton {...foundryButtonProps} disabled={props.disabled} onClick={onInternalClick}>
        {icon && <FAIcon icon={icon} spin={spin} additionalClasses="left" {...iconProps} />}
        <span className="btn-text">{label}</span>
        {rightIcon && <FAIcon icon={rightIcon} additionalClasses="right" {...rightIconProps} />}
        {children}
      </BootstrapButton>
      {tooltipAttributes?.tooltip && <InfoTooltip tooltipPosition="top" {...tooltipAttributes} />}
    </AccessControlWrapper>
  );
});

Button.displayName = 'ButtonLegacy';
